import {
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IMenuButton from "./type";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useActiveRouteName,
    isRouteActive
} from "../../../../helpers/navigation";
import {
    useItemPropsFromRowCard
} from "../../../rowCard/util";
import {
    ChevronRight as ChevronRightIcon,
    ChevronDown as ChevronDownIcon
} from "lucide-react-native";
import {
    getMenuButtonEndIcon,
    getMenuButtonTitle,
    navigateMenuButton
} from "../../util";
import {
    getMenuButtonTitleVariant,
    getMenuButtonGuideStyle
} from "../../stylesheet";
import {
    warnOnce
} from "../../../../utils/development";
import CollapseRegion from "../../../collapsible/internal/collapseRegion";
import AnimatedIcon from "../../../animatedIcon";
import Item from "../../../item";

const MenuButtonExpandedContext = createContext<boolean>(false);

const MenuButtonCollapseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const isExpanded = useContext(MenuButtonExpandedContext);

    return <AnimatedIcon
        isTransitioned={isExpanded}
        fromIcon={ChevronRightIcon}
        customColor={customColor}
        toIcon={ChevronDownIcon}
        iconTransition="morph"
        isMirrored={true}
        color={color}
        size={size}
    />;
};

const MenuButton: FC<IMenuButton> = ({
    activeRouteName: activeRouteNameProp,
    updateMenuButtonCollapse,
    updateMenuButtonCollabs,
    customLocalize,
    buttonIndex,
    closeAction,
    customTheme,
    buttonItem,
    navigation,
    depthMap,
    id
}) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const currentUpdateMenuButtonCollapse = updateMenuButtonCollapse ?? updateMenuButtonCollabs;

    const ownActiveRouteName = useActiveRouteName({
        navigation: activeRouteNameProp === undefined ? navigation : undefined
    });

    const isExpanded = !buttonItem.isCollapsible || !!buttonItem.isCollapse;

    const activeRouteName = activeRouteNameProp ?? ownActiveRouteName;

    const depth = depthMap.length - 1;

    const guideStyle = getMenuButtonGuideStyle({
        borders,
        colors,
        spaces
    });

    const isSelected = isRouteActive({
        item: buttonItem,
        activeRouteName
    });

    useEffect(() => {
        if(updateMenuButtonCollabs === undefined) {
            return;
        }

        warnOnce({
            message: "MenuButton: \"updateMenuButtonCollabs\" is deprecated and will be removed in the next major version. Use \"updateMenuButtonCollapse\" instead."
        });
    }, [
        updateMenuButtonCollabs
    ]);

    const onPress = () => {
        if(buttonItem.isCollapsible) {
            currentUpdateMenuButtonCollapse?.({
                status: !buttonItem.isCollapse,
                buttonItem,
                depthMap
            });

            return;
        }

        navigateMenuButton({
            closeAction,
            buttonItem,
            navigation
        });
    };

    const {
        children: itemChildren,
        itemProps
    } = useItemPropsFromRowCard({
        ...buttonItem.props,
        "aria-expanded": buttonItem.isCollapsible ? isExpanded : buttonItem.props?.["aria-expanded"],
        titleVariant: buttonItem.props?.titleVariant ?? getMenuButtonTitleVariant({
            depth
        }),
        borderRadius: buttonItem.props?.borderRadius ?? radiuses.actions,
        customTheme: buttonItem.props?.customTheme ?? customTheme,
        isSelected: buttonItem.props?.isSelected ?? isSelected,
        icon: buttonItem.icon ?? buttonItem.props?.icon,
        isInList: buttonItem.props?.isInList ?? true,
        rightIcon: getMenuButtonEndIcon({
            collapsibleIcon: MenuButtonCollapseIcon,
            buttonItem
        }),
        style: buttonItem.props?.style,
        title: getMenuButtonTitle({
            buttonItem,
            localize
        }),
        onPress
    });

    const renderSubButtons = () => {
        if(!buttonItem.subButtons || !buttonItem.subButtons.length) {
            return null;
        }

        return <CollapseRegion
            customTheme={customTheme}
            isOpen={isExpanded}
        >
            <View
                style={guideStyle}
            >
                {buttonItem.subButtons.map((subButtonItem, subButtonIndex) => {
                    return <MenuButton
                        key={`menu-sub-button-${id}-${depthMap.join("-")}-${subButtonIndex}`}
                        updateMenuButtonCollapse={currentUpdateMenuButtonCollapse}
                        activeRouteName={activeRouteName}
                        customLocalize={customLocalize}
                        buttonIndex={subButtonIndex}
                        buttonItem={subButtonItem}
                        closeAction={closeAction}
                        customTheme={customTheme}
                        navigation={navigation}
                        depthMap={[
                            ...depthMap,
                            subButtonIndex
                        ]}
                        id={id}
                    />;
                })}
            </View>
        </CollapseRegion>;
    };

    return <View
        key={`menu-button-${id}-${buttonIndex}`}
    >
        <MenuButtonExpandedContext.Provider
            value={isExpanded}
        >
            <Item
                {...itemProps}
                size={depth > 0 ? "small" : itemProps.size}
            >
                {itemChildren}
            </Item>
        </MenuButtonExpandedContext.Provider>
        {renderSubButtons()}
    </View>;
};
export default MenuButton;
